import type { User as Auth0User } from "@auth0/auth0-react";
import { parseWhiteboardDocumentId } from "@src/components/collaboration/document-id";
import {
	clearDemoRegistrationContext,
	extractDemoRegistrationContext,
	getStoredDemoRegistrationContext,
	saveDemoRegistrationContext,
} from "@src/components/demo-board/onboarding-demo-store";
import { registerUserMutationFn } from "@src/components/user/mutations/register-user";
import { findUserByEmailQueryFn } from "@src/components/user/queries/find-user-query";
import { UserActivationStatus } from "@src/gql/graphql";
import {
	captureBoardCreationSucceeded,
	captureUserSignedUp,
	getAuthProvider,
} from "@src/utils/analytics";
import { getStoredReferralCode } from "@src/utils/referral-tracker";
import posthog from "posthog-js";
import { userInsertSchema, userStoreSchema } from "./schema";

function getDemoRegistrationContext() {
	if (typeof window === "undefined") {
		return {
			demoSessionId: undefined,
			demoWhiteboardId: undefined,
		};
	}

	const fromUrl = extractDemoRegistrationContext(new URL(window.location.href));
	const fromStorage = getStoredDemoRegistrationContext();
	const resolved = {
		demoSessionId: fromUrl.demoSessionId ?? fromStorage?.demoSessionId,
		demoWhiteboardId: fromUrl.demoWhiteboardId ?? fromStorage?.demoWhiteboardId,
	};

	if (resolved.demoSessionId || resolved.demoWhiteboardId) {
		saveDemoRegistrationContext(resolved);
	}

	return resolved;
}

export async function getUserFromAuth0User(auth0User: Auth0User) {
	const userInfo = userInsertSchema.parse({
		firstName: auth0User.given_name || auth0User.name || "",
		lastName: auth0User.family_name || "",
		email: auth0User.email,
		externalId: auth0User.sub,
		idProvider: "auth0",
		avatarUrl: auth0User.picture || "",
	});

	const { data, errors } = await findUserByEmailQueryFn(userInfo.email);

	if (errors) {
		throw errors;
	}

	const user = data?.users?.users?.nodes?.[0];

	//we should register invited users
	if (user) {
		//when users are invited, we don't know their names yet
		//setting empty values to not change other code requiring name
		//from google authorization
		user.firstName ??= "";
		user.lastName ??= "";

		if (user.activationStatus !== UserActivationStatus.Invited) {
			clearDemoRegistrationContext();
			return userStoreSchema.parse(user);
		}
	}

	// Get referral code from storage if available
	const referralCode = getStoredReferralCode();
	const { demoSessionId, demoWhiteboardId } = getDemoRegistrationContext();

	let registerUser:
		| Awaited<ReturnType<typeof registerUserMutationFn>>["data"]
		| undefined;
	try {
		const result = await registerUserMutationFn({
			...userInfo,
			referralCode,
			demoSessionId,
			demoWhiteboardId,
		});
		if (result.errors?.length) {
			throw result.errors;
		}
		registerUser = result.data;
	} catch (err) {
		const message =
			err instanceof Error
				? err.message
				: Array.isArray(err)
					? (err as { message?: string }[])
							.map((e) => e?.message ?? "")
							.join(" ")
					: String(err);
		const alreadyRegistered =
			message.toLowerCase().includes("already registered") ||
			message.toLowerCase().includes("already exists") ||
			message.toLowerCase().includes("already provisioned");
		if (!alreadyRegistered) {
			throw err;
		}
		// User was pre-registered (e.g. provisioned by a test). The backend
		// waits for the projection before returning, so one network-only fetch
		// is enough; no polling loop needed.
		const { data: existingUser } = await findUserByEmailQueryFn(
			userInfo.email,
			"network-only",
		);
		const found = existingUser?.users?.users?.nodes?.[0];
		if (found) {
			clearDemoRegistrationContext();
			return userStoreSchema.parse({
				...found,
				firstName: found.firstName ?? "",
				lastName: found.lastName ?? "",
			});
		}
		throw err;
	}

	posthog.identify(userInfo.email, {
		email: userInfo.email,
		name: `${userInfo.firstName} ${userInfo.lastName}`.trim() || userInfo.email,
		firstName: userInfo.firstName,
		lastName: userInfo.lastName,
		avatarUrl: userInfo.avatarUrl,
	});
	captureUserSignedUp(
		getAuthProvider(auth0User.sub),
		Boolean(demoWhiteboardId),
		Boolean(referralCode),
	);

	if (demoWhiteboardId) {
		captureBoardCreationSucceeded(
			parseWhiteboardDocumentId(demoWhiteboardId),
			"from_demo",
		);
	}

	clearDemoRegistrationContext();

	return userStoreSchema.parse({
		...registerUser,
		id: registerUser?.user.register?.id,
		displayName: null,
		language: null,
		timezone: null,
		...registerUser?.user.register?.user,
	});
}
